import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { createPortal } from "react-dom";
import { NAV_SERVICES, NAV_MAIN, SERVICE_LINKS } from "@/data/site";
import { INFO_PAGES } from "@/data/infoPages";
import { ServiceIcon } from "./ServiceIcon";

const tid = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const ServicesMenu = ({ open }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.25 }} data-testid="services-dropdown"
        className="absolute left-1/2 top-full z-50 w-[660px] -translate-x-1/2 pt-3">
        <div className="grid grid-cols-2 gap-1 rounded-3xl border border-white/10 bg-[#1B0938]/95 p-3 shadow-2xl backdrop-blur-xl">
          {NAV_SERVICES.map(([slug, title]) => {
            const meta = SERVICE_LINKS.find((s) => s.slug === slug) || INFO_PAGES[slug];
            return (
              <Link key={slug} to={`/${slug}`} data-testid={`nav-service-${slug}`}
                className="group flex gap-3 rounded-2xl p-3 transition-colors hover:bg-white/5">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300 transition-colors group-hover:bg-[#7C3AED] group-hover:text-white">
                  <ServiceIcon name={slug === "digital-marketing-audits" ? "Stethoscope" : meta?.icon} className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-white">{title}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-violet-200">{meta?.short}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const MobileMenu = ({ onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="mobile-menu"
    className="fixed inset-0 z-[60] overflow-y-auto bg-[#13052A] px-6 pb-12 pt-5 lg:hidden">
    <div className="flex items-center justify-between">
      <Logo className="h-10" />
      <button onClick={onClose} aria-label="Close menu" data-testid="mobile-menu-close" className="rounded-full border border-white/20 p-2.5 text-white"><X className="h-5 w-5" /></button>
    </div>
    <p className="eyebrow mt-10 text-violet-300">Services</p>
    <ul className="mt-4 space-y-1">
      {NAV_SERVICES.map(([slug, title]) => (
        <li key={slug}><Link to={`/${slug}`} className="block py-2 text-lg font-semibold text-white" data-testid={`mobile-nav-${slug}`}>{title}</Link></li>
      ))}
    </ul>
    <div className="my-6 h-px bg-white/10" />
    <ul className="space-y-1">
      {NAV_MAIN.map(([to, label]) => (
        <li key={to}><Link to={to} className="block py-2 text-2xl font-bold text-white" data-testid={`mobile-nav-${tid(label)}`}>{label}</Link></li>
      ))}
    </ul>
    <Link to="/contact?type=audit" className="btn-primary mt-8 w-full" data-testid="mobile-menu-audit-btn">Get a Free Digital Audit <ArrowUpRight className="h-4 w-4" /></Link>
  </motion.div>
);

export const Nav = () => {
  const [svcOpen, setSvcOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, search } = useLocation();

  useEffect(() => { setSvcOpen(false); setMobile(false); }, [pathname, search]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${scrolled ? "bg-[#13052A]/85 py-2 shadow-[0_10px_40px_-20px_rgba(0,0,0,.6)] backdrop-blur-xl" : "bg-transparent py-4"}`}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-[#13052A]">Skip to content</a>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 sm:px-8">
        <Logo className={scrolled ? "h-10" : "h-12"} />
        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
            <button aria-expanded={svcOpen} onClick={() => setSvcOpen((v) => !v)} data-testid="nav-services-btn"
              className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-violet-100 transition-colors hover:text-white">
              Services <ChevronDown className={`h-4 w-4 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            <ServicesMenu open={svcOpen} />
          </div>
          {NAV_MAIN.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${tid(label)}`}
              className={({ isActive }) => `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "text-white" : "text-violet-100 hover:text-white"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact?type=audit" data-testid="nav-audit-cta" className="btn-accent hidden !px-5 !py-3 sm:inline-flex">
            Get a Free Digital Audit
          </Link>
          <button onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-btn" className="rounded-full border border-white/20 p-2.5 text-white lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      {createPortal(<AnimatePresence>{mobile && <MobileMenu onClose={() => setMobile(false)} />}</AnimatePresence>, document.body)}
    </header>
  );
};
